import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import endpoints from 'modules/Recruit/constants/endpoints';
import {
  getCandidateDocumentLinks,
  getCandidateTimeline,
  resendStageLink,
} from 'modules/Recruit/services/recruit';
import { AlertVariant } from 'modules/Recruit/types';
import { ApiResponse, CandidateTimelineData } from 'modules/Recruit/types/api';
import { notify } from 'modules/Retention/providers/NotificationProvider';
import React from 'react';
import { Loader } from 'subframe';
import { Button } from 'subframe/components/Button';
import { DocumentLink, downloadAllDocuments, DownloadProgress } from 'utils/downloadUtils';
import { CandidateTimelineSkeleton } from './CandidateTimelineSkeleton';
import { TimelineStep } from './TimelineStep';

interface CandidateTimelineProps {
  candidateId: number;
}

export const CandidateTimeline: React.FC<CandidateTimelineProps> = ({ candidateId }) => {
  const queryClient = useQueryClient();

  const [stageToRetrigger, setStageToRetrigger] = React.useState<string>('');

  const [downloadProgress, setDownloadProgress] = React.useState<DownloadProgress | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [downloadStatus, setDownloadStatus] = React.useState<string>('');

  const { data: candidateTimeline, isLoading: isLoadingTimeline } = useQuery({
    queryKey: [endpoints.candidateTimeline, candidateId],
    queryFn: () => getCandidateTimeline(candidateId),
    enabled: !!candidateId,
    select: (res: ApiResponse<CandidateTimelineData>) => res?.result?.candidate_timeline,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const timeline = candidateTimeline?.timeline || [];

  const { refetch: refetchDocumentLinks } = useQuery({
    queryKey: [endpoints.candidateDocumentLinks, candidateId],
    queryFn: () => getCandidateDocumentLinks(candidateId),
    enabled: false,
    gcTime: 2 * 60 * 1000, // 2 minutes
  });

  const retriggerStageMutation = useMutation({
    mutationFn: ({ step_name }: { step_name: string }) =>
      resendStageLink(candidateId, { step_name }),
    onSuccess: async (data: any, variables) => {
      notify({
        title:
          data?.result?.message || `Link for ${variables.step_name} has been resent successfully`,
        variant: AlertVariant.SUCCESS,
      });

      setStageToRetrigger('');

      // Refetch candidate timeline data
      await queryClient.invalidateQueries({ queryKey: [endpoints.candidateTimeline, candidateId] });
    },
    onError: (error: any, variables) => {
      notify({
        title:
          error?.result?.message ||
          `Link for ${variables.step_name} cound not be resent successfully`,
        variant: AlertVariant.ERROR,
      });
      setStageToRetrigger('');
    },
  });

  const handleStageRetrigger = (stepName: string) => {
    setStageToRetrigger(stepName);
    retriggerStageMutation.mutate({ step_name: stepName });
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setDownloadStatus('Fetching document links...');

      // Refetch document links to ensure we have the latest data
      const { data } = await refetchDocumentLinks();

      if (!data) {
        notify({
          title: 'Error',
          description: 'Failed to fetch document links',
          variant: AlertVariant.ERROR,
        });
        return;
      }

      const documents: DocumentLink[] = data?.result?.documents || [];

      if (documents.length === 0) {
        notify({
          title: 'Warning',
          description: 'No documents available for download',
          variant: AlertVariant.WARNING,
        });
        return;
      }

      setDownloadStatus(`Preparing to download ${documents.length} documents...`);

      await downloadAllDocuments(
        documents,
        `${candidateTimeline?.first_name}_${candidateTimeline?.last_name}`,
        (progress: DownloadProgress) => {
          setDownloadProgress(progress);
        }
      );
      setDownloadStatus('Creating zip file...');

      notify({
        title: 'Success',
        description: `Successfully downloaded ${documents.length} documents`,
        variant: AlertVariant.SUCCESS,
      });
    } catch (error: any) {
      notify({
        title: 'Error',
        description: error?.message || 'Failed to download documents. Please try again.',
        variant: AlertVariant.ERROR,
      });
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  const getDownloadButtonContent = () => {
    if (isDownloading) {
      return (
        <div className="flex items-center gap-2">
          <Loader size="small" />
          <span className="truncate max-w-[200px]">
            {downloadProgress
              ? `${downloadProgress.current}/${downloadProgress.total} - ${downloadProgress.fileName}`
              : downloadStatus || 'Downloading...'}
          </span>
        </div>
      );
    }
    return 'Download All';
  };

  if (isLoadingTimeline) {
    return <CandidateTimelineSkeleton />;
  }

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="flex w-full items-center justify-between">
        <span className="whitespace-pre-wrap text-section-header font-section-header text-default-font">
          Candidate Progress
        </span>
        <Button
          variant="brand-secondary"
          size="medium"
          icon={isDownloading ? undefined : 'FeatherFolderDown'}
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {getDownloadButtonContent()}
        </Button>
      </div>
      <div className="flex w-full flex-col items-start">
        {timeline.map((step, index) => (
          <TimelineStep
            key={step.stage_name}
            step={step}
            isLastStep={index === timeline.length - 1}
            onRetrigger={handleStageRetrigger}
            isRetriggering={stageToRetrigger != '' && stageToRetrigger === step.stage_name}
          />
        ))}
      </div>
    </div>
  );
};
